export enum InfoFindingCode {
  INFO_CSP_DEPENDENCIES = 'INFO_CSP_DEPENDENCIES',
  INFO_SPF_HARD_FAIL = 'INFO_SPF_HARD_FAIL',
  INFO_HOST_NOT_FOUND = 'INFO_HOST_NOT_FOUND',
  INFO_HTTP_TIMEOUT = 'INFO_HTTP_TIMEOUT',
  INFO_DNS_TIMEOUT = 'INFO_DNS_TIMEOUT',
  INFO_CHAIN_TLD_CHANGED = 'INFO_CHAIN_TLD_CHANGED',
  INFO_CHAIN_LIMIT_EXCEEDED = 'INFO_CHAIN_LIMIT_EXCEEDED',
  INFO_HTTP_5XX = 'INFO_HTTP_5XX',
  INFO_CONN_REFUSED = 'INFO_CONN_REFUSED',
  INFO_CONN_TIMEOUT = 'INFO_CONN_TIMEOUT',
  INFO_NO_ROUTE_TO_HOST = 'INFO_NO_ROUTE_TO_HOST',
  INFO_TLS_HANDSHAKE_ERROR = 'INFO_TLS_HANDSHAKE_ERROR',
  INFO_HSTS_HEADER_MALFORMED = 'INFO_HSTS_HEADER_MALFORMED',
  INFO_DNS_FAIL = 'INFO_DNS_FAIL',
  INFO_PORT_EXPOSED = 'INFO_PORT_EXPOSED',
  INFO_PORT_WAF = 'INFO_PORT_WAF',
  INFO_SCAN_TIMEOUT = 'INFO_SCAN_TIMEOUT',
}

// Note: Add port scan related codes here as well as above
export enum PortScanInfoFindingCode {
  INFO_PORT_EXPOSED = 'INFO_PORT_EXPOSED',
  INFO_PORT_WAF = 'INFO_PORT_WAF',
}

export type GenericInfoFinding = {
  code: InfoFindingCode;
};

// INFO_CSP_DEPENDENCIES
export type CSPDependencyInfoFinding = {
  code: InfoFindingCode.INFO_CSP_DEPENDENCIES;
  context: {
    csp: Record<string, { duplicate: boolean; fields: string[] }>;
  };
};

// INFO_SPF_HARD_FAIL
export type SPFHardFailInfoFinding = {
  code: InfoFindingCode.INFO_SPF_HARD_FAIL;
  context: {
    domains: string[];
  };
};

// Host not found info finding
export type HostNotFoundInfoFinding = {
  code: InfoFindingCode.INFO_HOST_NOT_FOUND;
};

// Timeout info findings
export type HTTPTimeoutInfoFinding = {
  code: InfoFindingCode.INFO_HTTP_TIMEOUT;
};

export type DnsErrorInfoFinding = {
  code: InfoFindingCode.INFO_DNS_FAIL | InfoFindingCode.INFO_DNS_TIMEOUT;
  context: {
    recordType: string | null;
  };
};

// Redirection info findings
export type ChainTLDChangedRedirectionInfoFinding = {
  code: InfoFindingCode.INFO_CHAIN_TLD_CHANGED;
  context: {
    via: string[];
  };
};
export type ChainLimitExceededRedirectionInfoFinding = {
  code: InfoFindingCode.INFO_CHAIN_LIMIT_EXCEEDED;
  context: {
    via: string[];
  };
};

// HTTP 5XX error info finding
export type HTTP5XXInfoFinding = {
  code: InfoFindingCode.INFO_HTTP_5XX;
  context: {
    statusCode: number;
    statusText: string;
  };
};

// Connection error info finding
export type ConnectionErrorInfoFinding = {
  code:
    | InfoFindingCode.INFO_NO_ROUTE_TO_HOST
    | InfoFindingCode.INFO_CONN_REFUSED
    | InfoFindingCode.INFO_CONN_TIMEOUT;
  context: {
    connectionType: string;
    port: string;
    value: string;
  };
};

// HSTS Header error finding
export type HSTSErrorInfoFinding = {
  code: InfoFindingCode.INFO_HSTS_HEADER_MALFORMED;
  context: {
    header: string;
  };
};

export type PortScanInfoFindingContext = {
  port: number;
  ips: string[];
  service: {
    name: string;
  };
  waf?: {
    name: string;
  };
};

// Port exposed info finding
export type PortExposedInfoFinding = {
  code: InfoFindingCode.INFO_PORT_EXPOSED;
  context: PortScanInfoFindingContext;
};

// Port WAF info finding
export type PortWafInfoFinding = {
  code: InfoFindingCode.INFO_PORT_WAF;
  context: PortScanInfoFindingContext;
};

export type InfoFinding =
  | GenericInfoFinding
  | CSPDependencyInfoFinding
  | SPFHardFailInfoFinding
  | HostNotFoundInfoFinding
  | HTTPTimeoutInfoFinding
  | DnsErrorInfoFinding
  | ChainTLDChangedRedirectionInfoFinding
  | ChainLimitExceededRedirectionInfoFinding
  | HTTP5XXInfoFinding
  | ConnectionErrorInfoFinding
  | HSTSErrorInfoFinding
  | PortExposedInfoFinding
  | PortWafInfoFinding;

// Type guard function
export function isFindingType<T extends InfoFinding>(
  finding: InfoFinding,
  expectedCode: T['code'],
): finding is T {
  return finding.code === expectedCode;
}

export function isPortScanInfoFinding(
  finding: InfoFinding,
): finding is PortExposedInfoFinding | PortWafInfoFinding {
  const portScanInfoFindingCodes = Object.values(PortScanInfoFindingCode);
  for (const code of portScanInfoFindingCodes) {
    if (isFindingType(finding, code as unknown as InfoFindingCode)) {
      return true;
    }
  }
  return false;
}

export function isConnectionErrorInfoFinding(
  finding: InfoFinding,
): finding is ConnectionErrorInfoFinding {
  const connectionErrorInfoFindingCodes = Object.values([
    InfoFindingCode.INFO_NO_ROUTE_TO_HOST,
    InfoFindingCode.INFO_CONN_REFUSED,
    InfoFindingCode.INFO_CONN_TIMEOUT,
  ]);
  for (const code of connectionErrorInfoFindingCodes) {
    if (isFindingType(finding, code as unknown as InfoFindingCode)) {
      return true;
    }
  }
  return false;
}
